// Componente PasswordInput reutilizable para la aplicación Amarsi Control Panel
// Extiende el componente Input con funcionalidad específica para contraseñas

import React, { useState, forwardRef } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { cn } from '../../utils/cn';
import Input from './Input';

/**
 * Componente PasswordInput
 * 
 * Campo de entrada especializado para contraseñas con:
 * - Toggle de visibilidad
 * - Indicador de fortaleza (opcional)
 * - Validación integrada
 */
const PasswordInput = forwardRef(({
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  showStrengthIndicator = false,
  placeholder = '••••••••',
  label = 'Contraseña',
  className = '',
  ...props
}, ref) => {
  // Estado para controlar la visibilidad de la contraseña
  const [showPassword, setShowPassword] = useState(false);

  // Función para alternar la visibilidad
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Calcula la fortaleza de la contraseña
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    
    // Longitud
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Complejidad
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    // Mapeo de score a etiqueta y color
    const strengthMap = {
      0: { label: '', color: '' },
      1: { label: 'Muy débil', color: 'bg-red-500' },
      2: { label: 'Débil', color: 'bg-orange-500' },
      3: { label: 'Regular', color: 'bg-yellow-500' },
      4: { label: 'Buena', color: 'bg-blue-500' },
      5: { label: 'Fuerte', color: 'bg-green-500' },
      6: { label: 'Muy fuerte', color: 'bg-green-600' },
    };

    return strengthMap[Math.min(score, 6)];
  };

  const passwordStrength = calculatePasswordStrength(value);

  // Icono para mostrar/ocultar contraseña
  const PasswordToggleIcon = showPassword ? FiEyeOff : FiEye;

  return (
    <div className="w-full">
      <Input
        ref={ref}
        type={showPassword ? 'text' : 'password'}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        disabled={disabled}
        icon="password"
        rightIcon={PasswordToggleIcon}
        onRightIconClick={togglePasswordVisibility}
        className={className}
        {...props}
      />

      {/* Indicador de fortaleza de la contraseña */}
      {showStrengthIndicator && value && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">Fortaleza de la contraseña</span>
            {passwordStrength.label && (
              <span className={cn(
                'text-xs font-medium',
                passwordStrength.color.replace('bg-', 'text-')
              )}>
                {passwordStrength.label}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <div
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                passwordStrength.color
              )}
              style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
            />
          </div>
        </div>
      )}

    </div>
  );
});

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
